import { userManagementApi } from './userManagementService';

// Types for Payments & Billing
export interface Payment {
  id: string;
  clientId: string;
  clientName: string;
  appointmentId?: string;
  items: PaymentItem[];
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  currency: string;
  status: 'pending' | 'paid' | 'partial' | 'overdue' | 'cancelled' | 'refunded';
  paymentMethod: 'cash' | 'card' | 'transfer' | 'voucher' | 'insurance';
  paidAmount: number;
  outstandingAmount: number;
  dueDate: string;
  paidAt?: string;
  notes?: string;
  invoiceNumber?: string;
  receiptNumber?: string;
  createdAt: string;
  updatedAt: string;
  clinicId: string;
}

export interface PaymentItem {
  id: string;
  type: 'service' | 'product' | 'consultation' | 'procedure' | 'medication';
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
  total: number;
  serviceId?: string;
  productId?: string;
}

export interface Invoice {
  id: string;
  paymentId: string;
  invoiceNumber: string;
  clientId: string;
  clientName: string;
  clientAddress: string;
  clientTaxNumber?: string;
  clinicName: string;
  clinicAddress: string;
  clinicTaxNumber: string;
  issueDate: string;
  dueDate: string;
  items: PaymentItem[];
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  notes?: string;
  createdAt: string;
  updatedAt: string;
  clinicId: string;
}

export interface PaymentFilters {
  search?: string;
  clientId?: string;
  status?: string;
  paymentMethod?: string;
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
  clinicId?: string;
  page?: number;
  limit?: number;
}

export interface CreatePaymentData {
  clientId: string;
  appointmentId?: string;
  items: Omit<PaymentItem, 'id'>[];
  discount?: number;
  paymentMethod: 'cash' | 'card' | 'transfer' | 'voucher' | 'insurance';
  paidAmount: number;
  dueDate: string;
  notes?: string;
  clinicId: string;
}

export interface UpdatePaymentData {
  items?: Omit<PaymentItem, 'id'>[];
  discount?: number;
  paymentMethod?: 'cash' | 'card' | 'transfer' | 'voucher' | 'insurance';
  paidAmount?: number;
  status?: 'pending' | 'paid' | 'partial' | 'overdue' | 'cancelled' | 'refunded';
  notes?: string;
}

export interface PaymentStats {
  totalPayments: number;
  totalRevenue: number;
  pendingPayments: number;
  overduePayments: number;
  averagePayment: number;
  paymentMethods: { [key: string]: number };
  monthlyRevenue: { month: string; revenue: number }[];
  topClients: { clientId: string; clientName: string; total: number }[];
}

export interface Voucher {
  id: string;
  code: string;
  name: string;
  description: string;
  type: 'percentage' | 'fixed';
  value: number;
  minAmount?: number;
  maxDiscount?: number;
  validFrom: string;
  validTo: string;
  usageLimit?: number;
  usedCount: number;
  isActive: boolean;
  clinicId: string;
}

class PaymentsService {
  private baseUrl = '/payments';

  async getPayments(filters: PaymentFilters = {}): Promise<{ payments: Payment[]; total: number; page: number; limit: number; stats: PaymentStats }> {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.clientId) params.append('clientId', filters.clientId);
      if (filters.status) params.append('status', filters.status);
      if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.amountMin) params.append('amountMin', filters.amountMin.toString());
      if (filters.amountMax) params.append('amountMax', filters.amountMax.toString());
      if (filters.clinicId) params.append('clinicId', filters.clinicId);
      if (filters.page != null) params.append('page', String(filters.page));
      if (filters.limit != null) params.append('limit', String(filters.limit));

      const response = await userManagementApi.get(`${this.baseUrl}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching payments:', error);
      throw error;
    }
  }

  async getPaymentById(id: string): Promise<Payment> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching payment:', error);
      throw error;
    }
  }

  async createPayment(data: CreatePaymentData): Promise<Payment> {
    try {
      const response = await userManagementApi.post(this.baseUrl, data);
      return response.data;
    } catch (error) {
      console.error('Error creating payment:', error);
      throw error;
    }
  }

  async updatePayment(id: string, data: UpdatePaymentData): Promise<Payment> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating payment:', error);
      throw error;
    }
  }

  async deletePayment(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Error deleting payment:', error);
      throw error;
    }
  }

  async processPayment(id: string, amount: number, method: string): Promise<Payment> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/${id}/process`, {
        amount,
        method
      });
      return response.data;
    } catch (error) {
      console.error('Error processing payment:', error);
      throw error;
    }
  }

  async refundPayment(id: string, amount: number, reason: string): Promise<Payment> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/${id}/refund`, {
        amount,
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Error refunding payment:', error);
      throw error;
    }
  }

  async getInvoices(filters: { clientId?: string; status?: string; dateFrom?: string; dateTo?: string } = {}): Promise<Invoice[]> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await userManagementApi.get(`${this.baseUrl}/invoices?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  }

  async createInvoice(paymentId: string): Promise<Invoice> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/invoices`, { paymentId });
      return response.data;
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  }

  async sendInvoice(id: string): Promise<void> {
    try {
      await userManagementApi.post(`${this.baseUrl}/invoices/${id}/send`);
    } catch (error) {
      console.error('Error sending invoice:', error);
      throw error;
    }
  }

  async downloadInvoice(id: string): Promise<Blob> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/invoices/${id}/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading invoice:', error);
      throw error;
    }
  }

  async getVouchers(clinicId: string): Promise<Voucher[]> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/vouchers?clinicId=${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      throw error;
    }
  }

  async createVoucher(data: Omit<Voucher, 'id' | 'usedCount' | 'clinicId'> & { clinicId: string }): Promise<Voucher> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/vouchers`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating voucher:', error);
      throw error;
    }
  }

  async updateVoucher(id: string, data: Partial<Omit<Voucher, 'id' | 'clinicId'>>): Promise<Voucher> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/vouchers/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating voucher:', error);
      throw error;
    }
  }

  async deleteVoucher(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/vouchers/${id}`);
    } catch (error) {
      console.error('Error deleting voucher:', error);
      throw error;
    }
  }

  async validateVoucher(code: string, amount: number): Promise<{ valid: boolean; discount: number; message?: string }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/vouchers/validate`, {
        code,
        amount
      });
      return response.data;
    } catch (error) {
      console.error('Error validating voucher:', error);
      throw error;
    }
  }

  async getPaymentStats(clinicId: string): Promise<PaymentStats> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/stats?clinicId=${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching payment stats:', error);
      throw error;
    }
  }

  async exportPayments(filters: PaymentFilters = {}): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await userManagementApi.get(`${this.baseUrl}/export?${params.toString()}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting payments:', error);
      throw error;
    }
  }
}

export const paymentsService = new PaymentsService();
